import { useMemo, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Building2, User, Users, Plus, X, Wrench } from "lucide-react";
import { highlightMatch } from "../lib/highlightMatch";

const SEL = "h-12 w-full border-2 border-slate-300 rounded-lg px-3 bg-white text-base mt-1";
export const TIPO_ICON = { condominio: Building2, condomino: User, soggetto: Users, fornitore: Wrench };

export function ParteChips({ parti, onRemove, testid }) {
  if (!parti.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-2" data-testid={testid}>
      {parti.map((p, i) => {
        const Icon = TIPO_ICON[p.tipo] || Users;
        return (
          <span key={`${p.ref_id}-${i}`} className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1.5 rounded-full bg-indigo-50 border border-indigo-200 text-sm font-semibold text-indigo-900">
            <Icon className="w-4 h-4" />{p.nome}
            {p.dettaglio && <span className="text-xs text-indigo-500 font-normal">({p.dettaglio})</span>}
            <button type="button" onClick={() => onRemove(i)} className="hover:text-red-600 ml-0.5"><X className="w-4 h-4" /></button>
          </span>
        );
      })}
    </div>
  );
}

export function ParteSelector({ label, parti, setParti, condomini, residenti, residentiAll, soggetti, fornitori, sources, onNuovo, testid }) {
  const [source, setSource] = useState(sources[0].key);
  const [search, setSearch] = useState("");

  const { visible, totalAvail } = useMemo(() => {
    const mk = (arr) => arr || [];
    let items = [];
    if (source === "condominio" || source === "tutti")
      items = items.concat(mk(condomini).map((c) => ({ tipo: "condominio", ref_id: c.id, nome: c.nome, dettaglio: c.indirizzo || "" })));
    if (source === "condomino" || source === "tutti")
      items = items.concat(mk(residenti).map((r) => ({ tipo: "condomino", ref_id: r.id, nome: r.nome, dettaglio: r.condominio_nome || "" })));
    // Iter123k265 · sorgente "condomino_tutti" · elenco completo di tutti i
    // condòmini presenti in anagrafica, usata quando il filtro per condominio
    // del Cliente è attivo ma l'utente vuole poter scegliere anche fuori.
    if (source === "condomino_tutti")
      items = items.concat(mk(residentiAll || residenti).map((r) => ({ tipo: "condomino", ref_id: r.id, nome: r.nome, dettaglio: r.condominio_nome || "" })));
    if (source === "soggetto" || source === "tutti")
      items = items.concat(mk(soggetti).map((s) => ({ tipo: "soggetto", ref_id: s.id, nome: s.nome, dettaglio: s.citta || "" })));
    // Iter122k86 · Opzione A · sorgente "fornitore" (fornitori Studio: avvocati, periti, impianti…)
    if (source === "fornitore" || source === "tutti")
      items = items.concat(mk(fornitori).map((f) => ({ tipo: "fornitore", ref_id: f.id, nome: f.ragione_sociale || f.nome || "", dettaglio: f.categoria || f.settore || (Array.isArray(f.categorie) ? f.categorie.join(", ") : "") || f.citta || "" })));
    const q = search.trim().toLowerCase();
    if (q) items = items.filter((i) => (i.nome + " " + i.dettaglio).toLowerCase().includes(q));
    const chosen = new Set(parti.map((p) => `${p.tipo}:${p.ref_id}`));
    const filtered = items.filter((i) => !chosen.has(`${i.tipo}:${i.ref_id}`));
    // Iter122f · limite portato a 200 (era 30); hint mostrato se >200
    return { visible: filtered.slice(0, 200), totalAvail: filtered.length };
  }, [source, search, condomini, residenti, residentiAll, soggetti, fornitori, parti]);
  const options = visible;

  return (
    <div data-testid={testid} className="rounded-xl border-2 border-slate-200 bg-slate-50/60 p-4">
      <div className="font-bold text-slate-800">{label}</div>
      <ParteChips parti={parti} onRemove={(i) => setParti(parti.filter((_, j) => j !== i))} testid={`${testid}-chips`} />
      <div className="grid sm:grid-cols-2 gap-3 mt-3">
        <div>
          <Label className="text-xs uppercase text-slate-500">Tipologia</Label>
          <select data-testid={`${testid}-source`} value={source} onChange={(e) => setSource(e.target.value)} className={SEL}>
            {sources.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase text-slate-500">Ricerca</Label>
          <Input data-testid={`${testid}-search`} value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per nome..." className="h-12 mt-1" />
        </div>
      </div>
      {(search || options.length > 0) && (
        <div className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-slate-200 bg-white divide-y divide-slate-100">
          {options.length === 0 ? (
            <div className="px-3 py-2.5 text-sm text-slate-400">Nessun risultato</div>
          ) : options.map((o) => {
            const Icon = TIPO_ICON[o.tipo] || Users;
            return (
              <button key={`${o.tipo}:${o.ref_id}`} type="button" data-testid={`${testid}-opt-${o.ref_id}`}
                onClick={() => setParti([...parti, o])}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm hover:bg-indigo-50">
                <Icon className="w-4 h-4 text-indigo-700 shrink-0" />
                <span className="font-semibold text-slate-800">{highlightMatch(o.nome, search)}</span>
                {o.dettaglio && <span className="text-slate-400 text-xs truncate">{highlightMatch(o.dettaglio, search)}</span>}
                <Plus className="w-4 h-4 text-indigo-600 ml-auto shrink-0" />
              </button>
            );
          })}
        </div>
      )}
      {totalAvail > options.length && (
        <div className="mt-1.5 text-xs text-amber-700 italic" data-testid={`${testid}-more-hint`}>
          Mostrati {options.length} di {totalAvail} · usa la ricerca per filtrare gli altri
        </div>
      )}
      {onNuovo && (
        <Button type="button" variant="outline" size="sm" data-testid={`${testid}-nuovo`} onClick={onNuovo}
          className="mt-3 border-indigo-300 text-indigo-800 hover:bg-indigo-50">
          <Plus className="w-4 h-4 mr-1" /> Nuovo soggetto
        </Button>
      )}
    </div>
  );
}
